import { Link } from "react-router-dom";
import { useState } from "react";
import { logAnalyticsEvent } from "../Firebase";
import LegalModal from "../components/LegalModal";
import "../styles/Footer.css";

export default function Footer() {
  const year = new Date().getFullYear();
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalType, setLegalType] = useState(null); // "privacy" | "terms"

  const EFFECTIVE_DATE = "January 3, 2026"; // change anytime
  const SITE = "crownedroofing.com";

  const openLegal = (type) => {
    setLegalType(type);
    setLegalOpen(true);

    // Optional analytics event
    try {
      logAnalyticsEvent("legal_open", { type, location: "footer" });
    } catch (e) {
      // ignore analytics errors
    }
  };

  const closeLegal = () => {
    setLegalOpen(false);
    // keep type so reopening feels instant; or clear it:
    // setLegalType(null);
  };

  const legalTitle =
    legalType === "privacy"
      ? "Privacy Policy"
      : legalType === "terms"
      ? "Terms of Service"
      : "Legal";

  return (
    <footer className="site-footer">
      <div className="footer-inner">
        {/* Brand / Overview */}
        <div className="footer-col">
          <h3 className="footer-logo">Crowned Roofing</h3>
          <p className="footer-text">
            Crowned Roofing provides commercial roof inspections, repair, and
            installation for warehouses, retail centers, and industrial
            facilities. Our inspection-first approach ensures clear
            documentation, informed decisions, and dependable results.
          </p>
        </div>

        {/* Services */}
        <div className="footer-col">
          <h4>Services</h4>
          <ul>
            <li>
              <Link to="/inspections">Roof Inspections</Link>
            </li>
            <li>
              <Link to="/commercial">Commercial Roofing</Link>
            </li>
            <li>
              <Link to="/residential">Residential Roofing</Link>
            </li>
            <li>
              <Link to="/faq">Roofing FAQ</Link>
            </li>
          </ul>
        </div>

        {/* Industries */}
        <div className="footer-col">
          <h4>Industries</h4>
          <ul>
            <li>Warehouses & Distribution</li>
            <li>Retail & Shopping Centers</li>
            <li>Manufacturing Facilities</li>
            <li>Office & Multi-Tenant Buildings</li>
            <li>Industrial Properties</li>
          </ul>
        </div>

        {/* Contact / CTA */}
        <div className="footer-col">
          <h4>Contact</h4>
          <p>Serving Toronto and surrounding areas</p>

          <p>
            <strong>Email:</strong>{" "}
            <a
              href="mailto:info@crownedroofing.com"
              onClick={() =>
                logAnalyticsEvent("email_click", {
                  location: "footer",
                })
              }
            >
              info@crownedroofing.com
            </a>
          </p>

          <p>
            <strong>Phone:</strong>{" "}
            <a
              href="tel:+14374353185"
              onClick={() =>
                logAnalyticsEvent("phone_click", {
                  location: "footer",
                })
              }
            >
              (437) 435 3185
            </a>
          </p>

          <Link
            to="/inspections"
            className="btn btn-primary footer-cta"
            onClick={() =>
              logAnalyticsEvent("inspection_cta_click", {
                source: "footer",
              })
            }
          >
            Request an Inspection
          </Link>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <p>© {year} Crowned Roofing. All rights reserved.</p>

        <p className="footer-legal">
          <button
            type="button"
            className="footer-legal-link"
            onClick={() => openLegal("privacy")}
          >
            Privacy Policy
          </button>
          <span className="footer-legal-sep">•</span>
          <button
            type="button"
            className="footer-legal-link"
            onClick={() => openLegal("terms")}
          >
            Terms of Service
          </button>
        </p>
      </div>

      <LegalModal open={legalOpen} title={legalTitle} onClose={closeLegal}>
  {legalType === "privacy" && (
    <div className="legal-doc">
      <h2>Privacy Policy</h2>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p>
        Crowned Roofing (“we,” “our,” or “us”) respects your privacy and is committed to
        protecting the personal information you share with us. This Privacy Policy explains
        how information is collected, used, and safeguarded when you visit <strong>{SITE}</strong>{" "}
        or submit information through our website.
      </p>

      <h4>Information We Collect</h4>
      <p><strong>Information You Provide Voluntarily</strong></p>
      <p>When you submit a contact or inspection request form, we may collect:</p>
      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Property or project details you choose to provide</li>
      </ul>
      <p>
        This information is stored securely in our internal systems to allow us to respond
        to your inquiry.
      </p>

      <p><strong>Automatically Collected Information</strong></p>
      <p>
        We use Google Analytics via Firebase Analytics to collect limited, non-identifying
        information such as pages visited, device/browser type, approximate geographic location,
        and interaction data. This helps us understand how visitors use our website and improve
        performance and usability.
      </p>

      <h4>How We Use Your Information</h4>
      <ul>
        <li>Respond to inquiries and inspection requests</li>
        <li>Communicate with you regarding requested services</li>
        <li>Improve website functionality and content</li>
        <li>Analyze website traffic and usage trends</li>
      </ul>
      <p>We do not sell, rent, or trade your personal information.</p>

      <h4>Communication Consent</h4>
      <p>
        By submitting a contact or inspection request form, you consent to being contacted
        by Crowned Roofing via phone, email, or text message regarding your inquiry or requested
        services. We only contact individuals who have voluntarily submitted their information.
      </p>

      <h4>Data Storage and Security</h4>
      <ul>
        <li>Contact form submissions are stored using Google Firebase / Firestore.</li>
        <li>Email notifications sent to our team do not contain submitted personal data.</li>
        <li>We use reasonable safeguards to protect stored information.</li>
      </ul>

      <h4>Third-Party Services</h4>
      <p>
        We currently use Google Analytics (Firebase Analytics) for website analytics. In the future,
        we may integrate a secure payment processor such as Stripe. Any payment information would be
        handled by the processor and not stored on our servers.
      </p>

      <h4>Your Choices</h4>
      <p>
        You may request access to or deletion of your submitted information, or request that we stop
        contacting you, by emailing <strong>info@crownedroofing.com</strong>.
      </p>

      <h4>Changes to This Policy</h4>
      <p>
        We may update this Privacy Policy from time to time. Updates will be posted with a revised
        effective date.
      </p>

      <h4>Contact Us</h4>
      <p>
        Crowned Roofing<br />
        Email: info@crownedroofing.com<br />
        Serving Toronto and surrounding areas
      </p>
    </div>
  )}

  {legalType === "terms" && (
    <div className="legal-doc">
      <h2>Terms of Service</h2>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p>
        These Terms of Service govern your use of <strong>{SITE}</strong> and any services or
        information provided through the website.
      </p>

      <h4>Use of the Website</h4>
      <p>
        This website is provided for informational and lead-generation purposes only. Use of this
        site does not create a contractor-client relationship until a written agreement is executed
        between you and Crowned Roofing.
      </p>

      <h4>Accuracy of Information</h4>
      <p>
        While we strive to provide accurate and current information, content on this website may not
        reflect all conditions, pricing, availability, or site-specific requirements. Roofing conditions
        and recommendations vary by property.
      </p>

      <h4>Inspection Requests and Estimates</h4>
      <ul>
        <li>Submitting a request does not guarantee service availability.</li>
        <li>Inspections, recommendations, and pricing are subject to site conditions and evaluation.</li>
        <li>Estimates are not binding unless provided in writing and accepted by both parties.</li>
      </ul>

      <h4>Limitation of Liability</h4>
      <p>
        To the fullest extent permitted by law, Crowned Roofing shall not be liable for indirect or
        consequential damages, losses arising from reliance on website content, or damages resulting from
        the use or inability to use this website.
      </p>

      <h4>Intellectual Property</h4>
      <p>
        All content on this website—including text, images, logos, reports, and designs—is the property of
        Crowned Roofing and may not be copied, reproduced, or distributed without written permission.
      </p>

      <h4>Third-Party Services</h4>
      <p>
        The website may reference or link to third-party services or platforms. Crowned Roofing is not responsible
        for the content, policies, or practices of third-party sites.
      </p>

      <h4>Modifications</h4>
      <p>
        We reserve the right to modify these Terms of Service at any time. Continued use of the website after changes
        are posted constitutes acceptance of the revised terms.
      </p>

      <h4>Governing Law</h4>
      <p>
        These terms are governed by the laws of the Province of Ontario, without regard to conflict of law principles.
      </p>

      <h4>Contact Information</h4>
      <p>
        Crowned Roofing<br />
        Email: info@crownedroofing.com
      </p>
    </div>
  )}
</LegalModal>
    </footer>
  );
}
